import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { TRANSLATIONS } from '../constants';
import { ShoppingBag, Minus, Plus, ShoppingCart, Trash2 } from 'lucide-react';
import { ProductDetailSkeleton } from '../components/Shimmer';
import { 
  updateMetaTags, 
  injectStructuredData, 
  generateProductStructuredData,
  generateBreadcrumbStructuredData 
} from '../utils/seo';
import { SEO_CONFIG, getFullUrl } from '../config/seo.config';
import { productsApi } from '../services/apiService';
import { Product } from '../types';

export const ProductDetails = () => {
  const { slugOrId } = useParams<{ slugOrId: string }>();
  const navigate = useNavigate();
  const { addToCart, removeFromCart, updateQuantity, language, categories, cart } = useStore();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const cartItem = product ? cart.find(item => item.id === product.id) : undefined;
  const quantityInCart = cartItem?.quantity || 0;
  const minQty = product?.minQuantity || 6;

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slugOrId) {
        setError(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(false);

      try {
        const response = await productsApi.getById(slugOrId);
        if (response.success && response.data) {
          const p = response.data as any;
          const transformedProduct: Product = {
            id: p.id,
            name: p.name,
            slug: p.slug,
            price: typeof p.price === 'string' ? parseFloat(p.price) : p.price,
            categoryId: p.category_id || p.categoryId,
            description: p.description,
            image: p.image,
            stock: p.stock,
            discount: p.discount || 0,
            featured: p.featured || false,
            minQuantity: p.min_quantity || p.minQuantity || 6,
          };
          setProduct(transformedProduct);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slugOrId]);

  useEffect(() => {
    if (product) {
      const productUrl = product.slug ? `/product/${product.slug}` : `/product/${product.id}`;
      
      // Update meta tags for product page
      updateMetaTags({
        title: `${product.name} - ${SEO_CONFIG.storeName}`,
        description: product.description,
        keywords: `${product.name}, ${product.categoryId}, buy online, ${SEO_CONFIG.storeName.toLowerCase()}`,
        canonical: getFullUrl(productUrl),
        ogImage: product.image,
        ogType: 'product',
      });

      // Inject product structured data
      const productData = generateProductStructuredData(product);
      
      // Generate breadcrumb data
      const category = categories.find(c => c.id === product.categoryId);
      const categoryUrl = category?.slug ? `/category/${category.slug}` : `/categories?cat=${product.categoryId}`;
      const breadcrumbs = generateBreadcrumbStructuredData([
        { name: 'Home', url: getFullUrl('/') },
        { name: category?.name[language] || 'Categories', url: getFullUrl(categoryUrl) },
        { name: product.name, url: getFullUrl(productUrl) },
      ]);

      injectStructuredData([productData, breadcrumbs]);
    }
  }, [product, language, categories]);

  if (loading) {
    return <ProductDetailSkeleton />;
  }

  if (error || !product) {
    return <div className="text-center py-20">Product not found</div>;
  }

  return (
    <main className="max-w-6xl mx-auto">
      <nav aria-label="Breadcrumb" className="mb-6">
        <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-900">
          &larr; {TRANSLATIONS.back[language]}
        </button>
      </nav>

      <article className="grid grid-cols-1 md:grid-cols-2 gap-12" itemScope itemType="https://schema.org/Product">
        <figure className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" itemProp="image" />
        </figure>

        <div className="space-y-8">
          <header>
            <span className="text-emerald-600 font-medium uppercase tracking-wide text-sm" itemProp="category">{product.categoryId}</span>
            <h1 className="text-3xl font-bold text-gray-900 mt-2" itemProp="name">{product.name}</h1>
            <p className="text-2xl font-semibold text-gray-900 mt-4" itemProp="offers" itemScope itemType="https://schema.org/Offer">
              <span itemProp="price">{product.price.toFixed(2)}</span> <span itemProp="priceCurrency" content="PLN">zł</span>
              <meta itemProp="availability" content="https://schema.org/InStock" />
            </p>
          </header>

          <p className="text-gray-600 leading-relaxed" itemProp="description">{product.description}</p>

          {quantityInCart > 0 ? (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <button 
                    onClick={() => {
                      if (quantityInCart <= minQty) {
                        removeFromCart(product.id);
                      } else {
                        updateQuantity(product.id, quantityInCart - minQty);
                      }
                    }}
                    className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
                    title={quantityInCart <= minQty ? TRANSLATIONS.remove[language] : "Decrease quantity"}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="flex-1 text-center text-lg font-semibold min-w-[60px]">
                    {quantityInCart}
                  </span>
                  <button 
                    onClick={() => addToCart(product, minQty)}
                    className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeFromCart(product.id)}
                    className="w-10 h-10 rounded-lg border border-red-200 bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 active:bg-red-200 transition-colors flex-shrink-0"
                    title={TRANSLATIONS.remove[language]}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => navigate('/checkout')}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors active:scale-95 flex-shrink-0"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span>{TRANSLATIONS.goToCheckout[language]}</span>
                </button>
              </div>
              <p className="text-sm text-emerald-600 font-medium text-center sm:text-left">
                {TRANSLATIONS.inCart[language]}: {quantityInCart} {language === 'en' ? 'pieces' : 'sztuk'}
              </p>
            </div>
          ) : (
            <button
              onClick={() => {
                addToCart(product, minQty);
              }}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-3 transition-all active:scale-[0.99] shadow-lg shadow-emerald-200"
            >
              <ShoppingBag className="w-5 h-5" />
              {TRANSLATIONS.addToCart[language]}
            </button>
          )}
        </div>
      </article>
    </main>
  );
};